import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ExportPanel } from '@/features/export/ExportPanel';
import { dateInputToUnix } from '@/shared/lib/dateInput';
import { formatUnixDateTime } from '@/shared/lib/datetime';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';
import { useListFilters } from '@/shared/lib/useListFilters';
import { DateTimeInput } from '@/shared/ui/DateTimeInput';
import { FilterActions } from '@/shared/ui/FilterActions';
import { FilterField } from '@/shared/ui/FilterField';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { Pagination } from '@/shared/ui/Pagination';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { SortableTh } from '@/shared/ui/SortableTh';
import { fetchBillingLogList, fetchBillingLogTypes, type BillingLogListParams } from './api';

interface BillingLogRow extends Record<string, unknown> {
  id: number | string;
  user?: { id?: number | string; name?: string; roles?: string[] } | null;
  wallet?: { id?: number | string } | null;
  object?: string | number | null;
  amount?: number | string;
  type_id?: number | string;
  create_time?: number;
  operator?: { name?: string } | null;
  comment?: string | null;
}

const SEARCH_KEY_OPTIONS = [
  { value: 'user_id', label: 'Поиск по id пользователя' },
  { value: 'object_id', label: 'Поиск по id объекта' },
  { value: 'type_id', label: 'Поиск по типу' },
  { value: 'id', label: 'Поиск по id' },
  { value: 'wallet_id', label: 'Поиск по wallet_id' },
  { value: 'amount', label: 'Поиск по сумме' },
  { value: 'operator_id', label: 'Поиск по operator_id' },
];

const OPERATION_TYPE_OPTIONS = [
  { value: '1', label: 'Пополнение' },
  { value: '0', label: 'Расход' },
];

const emptyFilters = {
  searchKey: '',
  searchValue: '',
  dateFrom: '',
  dateTo: '',
  operationType: '',
};

export const BillingLogPage = () => {
  const { draft, applied, page, setPage, setField, patchDraft, submit, reset } = useListFilters(emptyFilters);
  const [sort, setSort] = useState('');

  const typesQuery = useQuery({ queryKey: ['billing-log-types'], queryFn: fetchBillingLogTypes });

  const searchParam = (key: string) => (applied.searchKey === key ? applied.searchValue : '');

  const listParams: BillingLogListParams = {
    page,
    sort,
    user_id: searchParam('user_id'),
    object_id: searchParam('object_id'),
    type_id: searchParam('type_id'),
    id: searchParam('id'),
    wallet_id: searchParam('wallet_id'),
    amount: searchParam('amount'),
    operator_id: searchParam('operator_id'),
    operation_type: applied.operationType,
    create_time_from: dateInputToUnix(applied.dateFrom),
    create_time_to: dateInputToUnix(applied.dateTo),
  };

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['billing-log', listParams],
    queryFn: () => fetchBillingLogList(listParams),
  });

  const rows = getListRows<BillingLogRow>(data, ['items']);

  /** First click sorts descending (`-field`), then toggles. */
  const toggleSort = (field: string) => setSort((prev) => (prev === `-${field}` ? field : `-${field}`));

  const sortTh = (field: string, label: string) => (
    <SortableTh active={sort === field || sort === `-${field}`} onClick={() => toggleSort(field)}>
      {label}
    </SortableTh>
  );

  return (
    <>
      <section className="content-header">
        <h1>Логи</h1>
      </section>
      <section className="content">
        <ExportPanel path="billing-log" billingTypes={typesQuery.data ?? []} />

        <div className="box">
          <div className="box-header with-border">
            <h3 className="box-title">Список логов</h3>
            <form onSubmit={submit} style={{ marginTop: 12 }}>
              <FilterField label="Поиск">
                <FilterSelect
                  options={SEARCH_KEY_OPTIONS}
                  value={draft.searchKey}
                  onChange={(searchKey) => patchDraft({ searchKey, searchValue: '' })}
                />
                {draft.searchKey === 'type_id' ? (
                  <div style={{ marginTop: 6 }}>
                    <FilterSelect
                      options={typesQuery.data ?? []}
                      value={draft.searchValue}
                      isLoading={typesQuery.isLoading}
                      onChange={(v) => setField('searchValue', v)}
                    />
                  </div>
                ) : (
                  <input
                    className="form-control input-sm"
                    style={{ marginTop: 6, width: 250 }}
                    placeholder="Поиск..."
                    value={draft.searchValue}
                    onChange={(e) => setField('searchValue', e.target.value)}
                  />
                )}
              </FilterField>
              <FilterField label="От даты">
                <DateTimeInput
                  type="date"
                  value={draft.dateFrom}
                  max={draft.dateTo || undefined}
                  onChange={(v) => setField('dateFrom', v)}
                />
              </FilterField>
              <FilterField label="До даты">
                <DateTimeInput
                  type="date"
                  value={draft.dateTo}
                  min={draft.dateFrom || undefined}
                  onChange={(v) => setField('dateTo', v)}
                />
              </FilterField>
              <FilterField label="Фильтрация по типу оператора">
                <FilterSelect
                  options={OPERATION_TYPE_OPTIONS}
                  value={draft.operationType}
                  onChange={(v) => setField('operationType', v)}
                />
              </FilterField>
              <FilterActions disabled={isFetching} onReset={reset} />
            </form>
          </div>

          <div className="box-body">
            <QueryStatus isLoading={isLoading} error={error} errorText="Ошибка загрузки логов" />
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    {sortTh('id', 'id')}
                    <th>Пользователь</th>
                    <th>Роль</th>
                    <th>wallet</th>
                    <th>object</th>
                    {sortTh('amount', 'Сумма')}
                    <th>type_id</th>
                    {sortTh('create_time', 'Дата создания')}
                    <th>operator</th>
                    {sortTh('comment', 'Комментарии')}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.id}</td>
                      <td>
                        {row.user?.id ? <Link to={`/users/user/${row.user.id}`}>{row.user.name}</Link> : ''}
                      </td>
                      <td>{row.user?.roles?.[0] ?? ''}</td>
                      <td>{row.wallet?.id ?? ''}</td>
                      <td>{row.object ?? ''}</td>
                      <td>{row.amount}</td>
                      <td>{row.type_id}</td>
                      <td>{formatUnixDateTime(row.create_time)}</td>
                      <td>{row.operator?.name ?? ''}</td>
                      <td>{row.comment ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={getTotalPages(data)} onChange={setPage} />
          </div>
        </div>
      </section>
    </>
  );
};
